import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  NOTIFICATION_PREF_KEYS,
  type NotificationPrefKey,
  type NotificationPrefs,
} from "../../features/users/notificationPrefs";

interface NotificationPrefsSectionProps {
  value: NotificationPrefs;
  onChange: (prefs: NotificationPrefs) => void;
  isDarkMode: boolean;
  disabled?: boolean;
  translationNamespace?: "common" | "adminSections";
  /** Si false, la sección inicia colapsada (útil en formularios largos). */
  defaultExpanded?: boolean;
  collapsible?: boolean;
}

export function NotificationPrefsSection({
  value,
  onChange,
  isDarkMode,
  disabled = false,
  translationNamespace = "common",
  defaultExpanded = false,
  collapsible = true,
}: NotificationPrefsSectionProps) {
  const { t } = useTranslation(translationNamespace);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const textPrimary = isDarkMode ? "text-white" : "text-gray-900";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const cardBg = isDarkMode ? "bg-dark-surface" : "bg-gray-50";
  const borderColor = isDarkMode ? "border-dark-surface" : "border-gray-200";
  const hoverBg = isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100";

  const activeCount = NOTIFICATION_PREF_KEYS.filter(
    (key) => value[key]?.enabled === true,
  ).length;

  const handleToggle = (key: NotificationPrefKey, enabled: boolean) => {
    onChange({
      ...value,
      [key]: { enabled },
    });
  };

  const showContent = !collapsible || isExpanded;

  return (
    <div className={`rounded-lg border ${borderColor} overflow-hidden`}>
      {collapsible ? (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className={`w-full flex items-center justify-between gap-3 p-4 text-left transition-colors ${cardBg} ${hoverBg}`}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className={`text-sm font-semibold ${textPrimary}`}>
                {t("notificationPrefs.title", "Alertas de notificaciones")}
              </h3>
              {!isExpanded && activeCount > 0 && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-500/20 text-primary-600 dark:text-primary-400">
                  {t("notificationPrefs.activeCount", "{{count}} activas", {
                    count: activeCount,
                  })}
                </span>
              )}
            </div>
            <p className={`text-xs ${textSecondary} mt-1`}>
              {isExpanded
                ? t(
                    "notificationPrefs.description",
                    "Configura qué eventos de la flota quieres recibir como notificación.",
                  )
                : t(
                    "notificationPrefs.collapsedHint",
                    "Haz clic para ver y editar las alertas",
                  )}
            </p>
          </div>
          <i
            className={`ri-arrow-down-s-line text-xl ${textSecondary} shrink-0 transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            }`}
          ></i>
        </button>
      ) : (
        <div className={`p-4 ${cardBg}`}>
          <h3 className={`text-sm font-semibold ${textPrimary}`}>
            {t("notificationPrefs.title", "Alertas de notificaciones")}
          </h3>
          <p className={`text-xs ${textSecondary} mt-1`}>
            {t(
              "notificationPrefs.description",
              "Configura qué eventos de la flota quieres recibir como notificación.",
            )}
          </p>
        </div>
      )}

      {showContent && (
        <div className={`border-t ${borderColor}`}>
          {NOTIFICATION_PREF_KEYS.map((key, index) => (
            <div
              key={key}
              className={`flex items-center justify-between gap-3 p-4 ${cardBg} ${
                index < NOTIFICATION_PREF_KEYS.length - 1
                  ? `border-b ${borderColor}`
                  : ""
              }`}
            >
              <span className={`text-sm ${textPrimary} flex-1`}>
                {t(`notificationPrefs.items.${key}`, key)}
              </span>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={value[key]?.enabled ?? false}
                  disabled={disabled}
                  onChange={(e) => handleToggle(key, e.target.checked)}
                />
                <div className="w-11 h-6 bg-gray-300 dark:bg-gray-600 rounded-full peer peer-checked:bg-primary-500 transition-colors peer-focus:ring-2 peer-focus:ring-primary-500/40 peer-disabled:opacity-50 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-transform peer-checked:after:translate-x-5"></div>
              </label>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
